/*
 * FOCUS ONE PAGE BOX (2026-10-19). Chris: "I wonder if we could also have a feature where you double click a page slot box and it 'highlights' that box to show only the ribbons/lines coming
 * from it ... We would need to dim all other boxes at that point."
 *
 * The pure part, so each rule can be tested without a browser. The page is remembered by NAME, not by its slot: the boxes can be dragged into another order, and the focus belongs to the page.
 */
const PAIR_KEY = /^(\d+)-(\d+)$/;
const ARRIVAL_KEY = /^arrival:(\d+)$/;

/** The boxes a flow joins: both ends of a route, or the landing page of an arrival ("arrival:3"). */
export const flowBoxes = (key: string): number[] => {
  const arrival = ARRIVAL_KEY.exec(key);
  if (arrival) return [Number(arrival[1])];
  const pair = PAIR_KEY.exec(key);

  return pair ? [Number(pair[1]), Number(pair[2])] : [];
};

/** Whether a flow starts or ends at this box. */
export const flowTouches = (key: string, slot: number): boolean => flowBoxes(key).includes(slot);

/** Only the flows that touch the slot (every one of them: the focus is exactly where the usual busiest-twenty cut would hide the small ones). */
export const focusEntries = (entries: Array<[string, number]>, slot: number): Array<[string, number]> => entries.filter(([key, weight]) => weight > 0 && flowTouches(key, slot));

/** A double-click on a page's box: focus it, and a double-click on the page that is already focused lets go. */
export const toggleFocus = (current: string | null, page: string): string | null => (current === page ? null : page);

/**
 * Tells one click from the first half of a double-click. A single click on a box zooms into it, and a double-click is two clicks, so the zoom has to wait long enough to learn whether a second
 * click is coming. The click COUNT comes from the browser (MouseEvent.detail: 1 for the first click, 2 for the second), so how fast is "double" is the person's own system setting; this only
 * decides how long the single click waits. The timer functions are passed in so it can be tested.
 */
export const createClickGate = (delayMs: number, timers: { set: (fn: () => void, ms: number) => number; clear: (id: number) => void }) => {
  let pending: number | null = null;
  const cancel = () => {
    if (pending !== null) timers.clear(pending);
    pending = null;
  };

  return {
    /** the click count (event.detail); `single` runs after the delay unless a second click comes first, `double` runs at once on the second */
    click: (count: number, single: () => void, double: () => void) => {
      cancel();
      if (count >= 2) {
        double();

        return;
      }
      pending = timers.set(() => {
        pending = null;
        single();
      }, delayMs);
    },
    /** drop a waiting single click (the box was dragged, the view changed) */
    cancel,
    isWaiting: () => pending !== null,
  };
};
